'use strict';

import { updateUIFromShadowRoot } from '@core/i18n/window-language';
import {
    UPDATE_LANGUAGE_EVENT,
    SEARCH_REPORTS_RESULT_EVENT,
} from '@core/constants/EVENTS';
import { TRIGGER_CLOSE_TUTORIAL_TIP_EVENT } from '@core/constants/TUTORIAL_TIP';
import { globalSharedStyles, tooltipsStyles, typographySharedStyles } from '@core/styles';
import {
    CUSTOM_FLEX_GROW_ATTRIBUTE,
    DATA_TRANSLATE_ATTRIBUTE,
} from '@core/constants/ATTRIBUTES';
import { ROUTES } from '@core/constants/ROUTES';
import { ripple } from '@core/utils/ripple';
import { removeStoreValue, updateStoreValue, getStoreValue } from '@core/utils/store';
import { WESOG_STORE_PARAMS, TUTORIAL_TIP_MESSAGES_TYPES } from '@core/constants/STORE';
import { SEARCHER_TYPES } from '@core/constants/SEARCHER';
import {
    TOOLTIP_CONTENT_WIDTH_ATTRIBUTE,
    TOOLTIP_POSITION_ATTRIBUTE,
    TOOLTIP_POSITION,
} from '@components/TooltipComponent/CONSTANTS';

import { updateTooltipContent, applyTooltipPositioning } from './utils';
import htmlContent from './DefaultHomeIconComponent.html';
import cssContent from './DefaultHomeIconComponent.css';

/**
 * DefaultHomeIconComponent
 *
 * A web component that provides a default home icon with tooltip functionality.
 * Handles user authentication, default home operations, and provides visual feedback.
 *
 * @class DefaultHomeIconComponent
 * @extends {HTMLElement}
 */
class DefaultHomeIconComponent extends HTMLElement {
    static get observedAttributes() {
        return [
            CUSTOM_FLEX_GROW_ATTRIBUTE.toLowerCase(),
            TOOLTIP_CONTENT_WIDTH_ATTRIBUTE.toLowerCase(),
            TOOLTIP_POSITION_ATTRIBUTE.toLowerCase(),
        ];
    }

    constructor() {
        super();
        this.attachShadow({ mode: 'open' });

        /** @type {HTMLTemplateElement} */
        const template = document.createElement('template');
        template.innerHTML = htmlContent;

        const styleSheet = new CSSStyleSheet();
        styleSheet.replaceSync(cssContent);

        this.shadowRoot.adoptedStyleSheets = [
            globalSharedStyles,
            tooltipsStyles,
            typographySharedStyles,
            styleSheet,
        ];
        this.shadowRoot.appendChild(template.content.cloneNode(true));

        // Initialize properties
        this._customFlexGrow = 0;
        this._tooltipContentWidth = 0;
        this._tooltipPosition = TOOLTIP_POSITION.BOTTOM_LEFT;
        this.openEventTutorialTip = false;
        this.currentReportType = '';

        // Cache DOM references
        this._tooltipIcon = null;
        this._tooltipDropdownContent = null;
        this._tooltipDropdownContentText = null;
        this._tooltipImgsContentContainer = null;
        this._tooltipPlaceholder = null;
        this._defaultHomeIconTips = null;

        // Bind event handlers
        this._handleTranslationHelperBound = this._handleTranslationHelper.bind(this);
        this._handleClickBound = this._handleClick.bind(this);
        this._closeDefaultHomeIconTipsBound = this._closeDefaultHomeIconTips.bind(this);
        this._handleSearchReportsResultBound = this._handleSearchReportsResult.bind(this);
        this._handleHeaderContentTabMouseEnterBound =
            this._handleHeaderContentTabMouseEnter.bind(this);
        this._handleHeaderContentTabMouseLeaveBound =
            this._handleHeaderContentTabMouseLeave.bind(this);
    }

    /**
     * Called when the element is connected to the DOM
     * @returns {void}
     * @override
     */
    connectedCallback() {
        this._cacheDOMElements();
        this._showDefaultHomeIcon();
        this._addEventListeners();
        this._addDefaultHomeIconTipListener();
        this._addRippleBehavior();
        this.render();
    }

    /**
     * Called when the element is disconnected from the DOM
     * @returns {void}
     * @override
     */
    disconnectedCallback() {
        this._removeEventListeners();
    }

    /**
     * Called when observed attributes change
     * @param {string} name - The attribute name
     * @param {string} oldValue - The old attribute value
     * @param {string} newValue - The new attribute value
     * @returns {void}
     * @override
     */
    attributeChangedCallback(name, oldValue, newValue) {
        switch (name) {
            case CUSTOM_FLEX_GROW_ATTRIBUTE.toLowerCase():
                this._customFlexGrow = Number(newValue) || 0;
                break;
            case TOOLTIP_CONTENT_WIDTH_ATTRIBUTE.toLowerCase():
                this._tooltipContentWidth = Number(newValue) || 0;
                break;
            case TOOLTIP_POSITION_ATTRIBUTE.toLowerCase():
                this._tooltipPosition = newValue;
                break;
        }

        this.render(false);
    }

    /**
     * Cache DOM element references for better performance
     * @returns {void}
     * @private
     */
    _cacheDOMElements() {
        this._tooltipIcon = this.shadowRoot.querySelector('[data-default-view-icon]');
        this._tooltipDropdownContent =
            this.shadowRoot.getElementById('tooltip-home-content');
        this._tooltipDropdownContentText = this.shadowRoot.querySelector(
            '#tooltip-home-content > p',
        );
        this._tooltipImgsContentContainer = this.shadowRoot.querySelector(
            '[data-default-home-tooltip-dropdown]',
        );
        this._tooltipPlaceholder = this.shadowRoot.querySelector(
            '[data-default-home-tooltip-placeholder]',
        );
        this._defaultHomeIconTips = this.shadowRoot.querySelector(
            '[data-default-home-icon-tips]',
        );
    }

    /**
     * Render the component
     * @param {boolean} firstTime - Whether this is the first render
     * @returns {void}
     */
    render(firstTime = true) {
        if (
            !this._tooltipDropdownContent ||
            !this._tooltipDropdownContentText ||
            !this._tooltipImgsContentContainer
        ) {
            return;
        }

        // Apply tooltip content width
        if (this._tooltipContentWidth) {
            this._tooltipDropdownContent.style.maxWidth = `${this._tooltipContentWidth}px`;
        }

        // Update tooltip content
        updateTooltipContent(this._tooltipDropdownContentText);

        // Apply tooltip positioning
        applyTooltipPositioning(
            this._tooltipImgsContentContainer,
            this._tooltipPosition,
            this._customFlexGrow,
        );

        // Add click handler only on first render
        if (firstTime && this._tooltipPlaceholder) {
            this._tooltipPlaceholder.addEventListener('click', this._handleClickBound);
        }
    }

    /**
     * Handle click events on the tooltip icon
     * @param {Event} e - The click event
     * @returns {Promise<void>}
     * @private
     */
    async _handleClick(e) {
        e.preventDefault();
        e.stopPropagation();

        this._tooltipIcon.toggleAttribute('selected');

        if (this._tooltipIcon.hasAttribute('selected')) {
            this._tooltipIcon.removeAttribute('selected');
            removeStoreValue(WESOG_STORE_PARAMS.DEFAULT_HOME_REPORT_VIEW);
            this._tooltipDropdownContentText.setAttribute(
                DATA_TRANSLATE_ATTRIBUTE,
                'tooltip.defaultHome.selected',
            );
        } else {
            /** @type {string} */
            const currentUrl = window.location.pathname + window.location.search;

            this._tooltipIcon.setAttribute('selected', '');
            updateStoreValue(WESOG_STORE_PARAMS.DEFAULT_HOME_REPORT_VIEW, currentUrl);
            this._tooltipDropdownContentText.setAttribute(
                DATA_TRANSLATE_ATTRIBUTE,
                'tooltip.defaultHome.unselected',
            );
        }
        this._handleTranslationHelper();
    }

    /**
     * Translate the component elements
     * @returns {void}
     * @private
     */
    _handleTranslationHelper() {
        updateUIFromShadowRoot(this.shadowRoot);
    }

    /**
     * Add event listeners to the component elements
     * @returns {void}
     * @private
     */
    _addEventListeners() {
        document.addEventListener(
            UPDATE_LANGUAGE_EVENT,
            this._handleTranslationHelperBound,
        );

        document.addEventListener(
            TRIGGER_CLOSE_TUTORIAL_TIP_EVENT,
            this._closeDefaultHomeIconTipsBound,
        );

        document.addEventListener(
            SEARCH_REPORTS_RESULT_EVENT,
            this._handleSearchReportsResultBound,
        );
    }

    /**
     * Handle the search reports result event.
     * @param {Event} event - The event object.
     * @returns {void}
     * @private
     */
    _handleSearchReportsResult(event) {
        this.currentReportType = event.detail.reportFilter;
    }

    /**
     * Close the default home icon tips.
     * @returns {void}
     * @private
     */
    _closeDefaultHomeIconTips() {
        if (this.openEventTutorialTip) {
            this.openEventTutorialTip = false;

            return;
        }

        this._closeDefaultHomeIconTipsComponent();
    }

    /**
     * Remove event listeners to prevent memory leaks
     * @returns {void}
     * @private
     */
    _removeEventListeners() {
        document.removeEventListener(
            UPDATE_LANGUAGE_EVENT,
            this._handleTranslationHelperBound,
        );

        if (this._tooltipPlaceholder) {
            this._tooltipPlaceholder.removeEventListener('click', this._handleClickBound);
        }

        document.removeEventListener(
            TRIGGER_CLOSE_TUTORIAL_TIP_EVENT,
            this._closeDefaultHomeIconTipsBound,
        );

        document.removeEventListener(
            SEARCH_REPORTS_RESULT_EVENT,
            this._handleSearchReportsResultBound,
        );

        if (this._tooltipPlaceholder) {
            this._tooltipPlaceholder.removeEventListener(
                'mouseenter',
                this._handleHeaderContentTabMouseEnterBound,
            );

            this._tooltipPlaceholder.removeEventListener(
                'mouseleave',
                this._handleHeaderContentTabMouseLeaveBound,
            );
        }
    }

    /**
     * Add the ripple effect to elements with data-ripple attribute
     * @returns {void}
     * @private
     */
    _addRippleBehavior() {
        const rippleElements = this.shadowRoot.querySelectorAll('[data-ripple]');
        rippleElements.forEach((element) => {
            ripple(element);
        });
    }

    /**
     * Show the default home icon when the report type is not a common report.
     * @returns {void}
     * @private
     */
    _showDefaultHomeIcon() {
        if (!window.location.pathname.includes(ROUTES.REPORTS_LIST)) {
            return;
        }

        /** @type {URLSearchParams} */
        const urlParams = new URLSearchParams(window.location.search);

        this.currentReportType =
            this.currentReportType || urlParams.get(SEARCHER_TYPES.REPORT_TYPE) || '';

        this.style.display = 'inline-flex';
        /* check if the default view is selected and set the attribute */
        /** @type {string} */
        const defaultHomeView = getStoreValue(
            WESOG_STORE_PARAMS.DEFAULT_HOME_REPORT_VIEW,
        );
        /** @type {URLSearchParams} */
        const defaultHomeViewParams = new URLSearchParams(
            `?${defaultHomeView.split('?')[1]}`,
        );
        /** @type {boolean} */
        const isTheSameReportType =
            defaultHomeViewParams.get(SEARCHER_TYPES.REPORT_TYPE) ===
            this.currentReportType;

        if (isTheSameReportType) {
            this._tooltipIcon.setAttribute('selected', '');
            this._tooltipDropdownContentText.setAttribute(
                DATA_TRANSLATE_ATTRIBUTE,
                'tooltip.defaultHome.unselected',
            );
        } else {
            this._tooltipIcon.removeAttribute('selected');
            this._tooltipDropdownContentText.setAttribute(
                DATA_TRANSLATE_ATTRIBUTE,
                'tooltip.defaultHome.selected',
            );
        }
    }

    /**
     * Show the default home icon tips.
     * @returns {void}
     * @private
     */
    _showDefaultHomeIconTips() {
        const /** @type {Boolean|undefined} */ tutorialDefaultHomeIconTipValue =
                getStoreValue(WESOG_STORE_PARAMS.TUTORIAL_TIPS)?.[
                    TUTORIAL_TIP_MESSAGES_TYPES.DEFAULT_REPORT
                ];

        if (tutorialDefaultHomeIconTipValue) {
            this.openEventTutorialTip = true;
            document.dispatchEvent(new CustomEvent(TRIGGER_CLOSE_TUTORIAL_TIP_EVENT));
            this._defaultHomeIconTips.style.display = 'flex';
        }
    }

    /**
     * Add default home icon tips listener.
     * @returns {void}
     * @private
     */
    _addDefaultHomeIconTipListener() {
        if (!window.location.pathname.includes(ROUTES.REPORTS_LIST)) {
            return;
        }

        if (this._tooltipPlaceholder) {
            this._tooltipPlaceholder.addEventListener(
                'mouseenter',
                this._handleHeaderContentTabMouseEnterBound,
            );

            this._tooltipPlaceholder.addEventListener(
                'mouseleave',
                this._handleHeaderContentTabMouseLeaveBound,
            );
        }
    }

    /**
     * Close the default home icon tips.
     * @returns {void}
     * @private
     */
    _closeDefaultHomeIconTipsComponent() {
        this._defaultHomeIconTips.style.display = 'none';
    }

    /**
     * Handle the mouse enter event on the header content tab.
     * @returns {void}
     * @private
     */
    _handleHeaderContentTabMouseEnter() {
        this._showDefaultHomeIconTips();
    }

    /**
     * Handle the mouse leave event on the header content tab.
     * @param {Event} event - The event object.
     * @returns {void}
     * @private
     */
    _handleHeaderContentTabMouseLeave(event) {
        /** @type {object} */
        const bounds = this._tooltipPlaceholder.getBoundingClientRect();
        /** @type {number} */
        const mouseX = event.clientX;
        /** @type {number} */
        const mouseY = event.clientY;

        /* Check which side the mouse is leaving from */
        if (mouseX < bounds.left || mouseX > bounds.right || mouseY < bounds.top) {
            this._closeDefaultHomeIconTipsComponent();
        }
    }
}

customElements.define('default-home-icon-component', DefaultHomeIconComponent);
